// JOBE-0101: useEntityBrowserQuery — owns the fetch + cursor state for
// an EntityBrowser instance. Relocated and renamed from
// `library/useLibraryQuery.js`. Wraps `fetch('/api/entities?…')` and a
// small `(filterHash, cursor)`-keyed cache surviving for the route
// session (R11). Same fetch pattern as LibraryDocumentDetail.jsx —
// useEffect + AbortController, credentials: 'same-origin', no new
// dependency.
//
// JOBE-0101 addition: optional `anchor` prop. The host passes an
// object like `{ inProject: 'urn:jobe:project:p1' }`; the query
// merges every key as an extra `/api/entities` query parameter
// alongside chip-derived filters. Anchors are not URL-stateful — they
// are route-derived (per spec §Decision 2 table).

import { useEffect, useRef, useState } from 'react';
import { chipsToFetchQuery, hashFilters } from '../library/chipVocabulary.js';

export function useEntityBrowserQuery({ chips, sort, cursor, anchor }) {
  // `pages` is one entry per fetched page; rendered as `pages.flat()`.
  const [pages, setPages] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [kindSummary, setKindSummary] = useState({});
  const [loadState, setLoadState] = useState('idle'); // idle | loading | ready | error
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  // Route-session page cache. Keyed by `filterHash`; value is an array
  // of pages already pulled (in cursor order). Survives chip toggles
  // within the route session; cleared on route exit (component unmount).
  const cacheRef = useRef(new Map());

  // Stable anchor signature for the cache key + effect dep. Sorting
  // keys avoids re-fetch on shallow re-renders that re-emit the same
  // anchor with different property insertion order.
  const anchorKey = anchor
    ? Object.entries(anchor)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => `${k}=${v}`)
        .join('&')
    : '';

  // Track the current filterHash so cursor changes within the same
  // filter set extend the pages array rather than reset it. Anchor is
  // part of the filter identity.
  const filterHash = `${anchorKey}|${hashFilters(chips, sort)}`;
  const prevFilterHashRef = useRef(filterHash);

  useEffect(() => {
    const ctrl = new AbortController();
    let alive = true;

    const filterChanged = prevFilterHashRef.current !== filterHash;
    prevFilterHashRef.current = filterHash;

    // Cache hit short-circuit: when there's no cursor (page 0) and we
    // already have a cached entry for this filter, render from cache
    // without a network round-trip.
    if (!cursor) {
      const cached = cacheRef.current.get(filterHash);
      if (cached && cached.length > 0) {
        setPages(cached.map((p) => p.items));
        setNextCursor(cached[cached.length - 1].nextCursor || null);
        setKindSummary(cached[cached.length - 1].kindSummary || {});
        setLoadState('ready');
        setError(null);
        return () => {
          alive = false;
          ctrl.abort();
        };
      }
    }

    // Build the BFF query from chips, then merge in anchor keys.
    // chipsToFetchQuery's `extra` param handles the sort + cursor +
    // anchor merge in a single pass.
    const extra = {
      sort: sort || 'lastChangedAt:desc',
      ...(cursor ? { cursor } : {}),
    };
    if (anchor) {
      for (const [k, v] of Object.entries(anchor)) {
        if (v !== undefined && v !== null && v !== '') {
          extra[k] = String(v);
        }
      }
    }
    const params = chipsToFetchQuery(chips, extra);

    setLoadState('loading');
    setError(null);

    fetch(`/api/entities?${params.toString()}`, {
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
      .then(async (r) => {
        if (!alive) return;
        if (!r.ok) {
          setError({ status: r.status });
          setLoadState('error');
          return;
        }
        const j = await r.json();
        if (!alive) return;
        const page = {
          items: Array.isArray(j.items) ? j.items : [],
          nextCursor: j.nextCursor || null,
          kindSummary: j.kindSummary || {},
        };
        // Append-on-cursor / replace-on-filter-change semantics. The
        // cache stores the pages in cursor order so a back-out and
        // back-in restores the visible list.
        if (cursor && !filterChanged) {
          setPages((prev) => [...prev, page.items]);
          const list = cacheRef.current.get(filterHash) || [];
          cacheRef.current.set(filterHash, [...list, page]);
        } else {
          setPages([page.items]);
          cacheRef.current.set(filterHash, [page]);
        }
        setNextCursor(page.nextCursor);
        setKindSummary(page.kindSummary);
        setLoadState('ready');
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        setError({ status: 0, message: err?.message || String(err) });
        setLoadState('error');
      });

    return () => {
      alive = false;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterHash, cursor, tick]);

  return {
    pages,
    rows: pages.flat(),
    nextCursor,
    kindSummary,
    loadState,
    error,
    retry: () => setTick((t) => t + 1),
  };
}
